import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  BehaviorSubject,
  fromEvent,
  Observable,
  of,
  retry,
  share,
  Subject,
  timer
} from 'rxjs';
import { webSocket } from 'rxjs/webSocket';
import {
  filter,
  map,
  mergeMap,
  startWith,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { DocketService } from '../shared';
import { MatDialog } from '@angular/material/dialog';
import { SignableStage } from '../models/signature';
import { environment } from '../../environments/environment';
import { AsyncPipe, DOCUMENT, NgIf } from '@angular/common';
import { BookingService } from '../shared/services/booking.service';
import { BookingStatus } from '../models/booking-status';
import { SaleDocketService } from '../shared/services/sale-docket.service';
import { SignatureCaptureComponent } from '../shared/signature-capture/signature-capture.component';
import { ToastrService } from 'ngx-toastr';
import { MatIcon } from '@angular/material/icon';
import { BookingItemNamePipe } from '../shared/pipes/booking-item-name.pipe';

interface SignableResource {
  documentId: number;
  documentType: string;
  resourceType: 'docket' | 'booking' | 'saleDocket';
}

@Component({
  selector: 'app-counter',
  templateUrl: './counter.component.html',
  styleUrls: ['./counter.component.css'],
  providers: [
    DocketService,
    BookingService,
    SaleDocketService,
    ToastrService,
    BookingItemNamePipe
  ],
  standalone: true,
  imports: [AsyncPipe, NgIf, SignatureCaptureComponent, MatIcon]
})
export class CounterComponent implements OnInit, OnDestroy {
  connected = false;
  allowRetries = true;
  error: string;

  showTerminalCodeInstructions = true;

  terminalCode = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, '0');

  private signableResource = new BehaviorSubject<SignableResource | null>(null);

  showSignaturePane$ = this.signableResource.pipe(
    map((resource) => Boolean(resource))
  );
  private infoDisplayAndStage = this.signableResource.asObservable().pipe(
    tap((resource) => console.log('Resource', resource)),
    mergeMap((resource) => this.signableResourceToDisplay(resource)),
    share()
  );

  infoDisplay$ = this.infoDisplayAndStage.pipe(
    map(({ infoDisplay }) => infoDisplay)
  );
  signableStage$ = this.infoDisplayAndStage.pipe(map(({ stage }) => stage));

  signableId$ = this.signableResource
    .asObservable()
    .pipe(map((resource) => resource?.documentId));
  signableType$ = this.signableResource
    .asObservable()
    .pipe(
      map(
        (resource) =>
          resource?.documentType as 'docket' | 'booking' | 'saleDocket'
      )
    );
  private ngUnsubscribe = new Subject<void>();

  fullscreen$: Observable<boolean>;

  @ViewChild('container')
  container: ElementRef;

  constructor(
    private docketService: DocketService,
    private bookingService: BookingService,
    private saleDocketService: SaleDocketService,
    private toastr: ToastrService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.fullscreen$ = fromEvent(this.document, 'fullscreenchange').pipe(
      map(() => Boolean(this.document.fullscreenElement)),
      startWith(Boolean(this.document.fullscreenElement))
    );
  }

  fullScreen() {
    this.container.nativeElement.requestFullscreen();
  }

  ngOnInit(): void {
    this.reconnect();
  }

  connectToNotificationServer() {
    if (this.connected) {
      return;
    }
    const open = new Subject();
    const close = new Subject();
    return webSocket({
      url: environment.signature_socket_server
    })
      .multiplex(
        () => ({ type: 'init', code: this.terminalCode }),
        () => null,
        (message) => this.filterMessages(message)
      )
      .pipe(
        retry({
          delay: (error) => {
            this.unknownError(error);
            return timer(5000).pipe(take(1));
          }
        }),
        tap(() => this.connectionSuccessful()),
        map((message) => this.mapMessages(message)),
        filter((message) => Boolean(message)),
        tap((message: SignableResource | null) => {
          this.showTerminalCodeInstructions = false;
          this.signableResource.next(message);
        })
      );
  }

  private mapMessages(message: any) {
    if (
      'type' in message &&
      (message.type === 'clear' || message.type === 'ok')
    ) {
      return null;
    }
    if ('documentType' in message && 'documentId' in message) {
      return message as SignableResource;
    }
    return null;
  }

  private filterMessages(message: any) {
    if (
      'type' in message &&
      (message.type === 'clear' || message.type === 'ok')
    ) {
      return true;
    }
    if ('documentType' in message && 'documentId' in message) {
      return true;
    }
    return false;
  }

  reconnect() {
    this.connectToNotificationServer()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        complete: () => {
          this.connected = false;
        },
        error: (e) => {
          this.unknownError(e);
        }
      });
  }

  private connectionSuccessful() {
    this.connected = true;
    this.error = null;
  }

  private connectionFailed() {
    this.connected = false;
    this.error = 'Unable to connect to notifications server';
  }

  private unknownError(e: any) {
    console.error('Error in socket connection', e);
    this.connected = false;
    this.error = 'An error occurred during the handling of notifications';
  }

  private signableResourceToDisplay(
    resource: SignableResource | null
  ): Observable<{ infoDisplay: string; stage: SignableStage | null }> {
    if (!resource) {
      return of({ infoDisplay: '', stage: null });
    }
    if (resource.documentType === 'docket') {
      return this.docketService.getDocket(resource.documentId, [], []).pipe(
        map((docket) => {
          if (docket.account_reference) {
            return {
              stage: 'Invoice',
              infoDisplay: `Signing: Invoice ${docket.account_reference}`
            };
          }
          return {
            stage: 'Hire Contract',
            infoDisplay: `Signing: Hire Contract ${docket.id}`
          };
        })
      );
    }
    if (resource.documentType === 'booking') {
      return this.bookingService.getBooking(resource.documentId, []).pipe(
        map((booking) => {
          return {
            stage:
              booking.status === BookingStatus.REQUEST
                ? 'Quotation'
                : 'Booking',
            infoDisplay: `Signing: Booking ${booking.id}`
          };
        })
      );
    }
    if (resource.documentType === 'saleDocket') {
      return this.saleDocketService.getSaleDocket(resource.documentId).pipe(
        map((sale) => {
          return {
            stage: 'Sale',
            infoDisplay: sale.account_reference
              ? `Signing: Sale Invoice ${sale.account_reference}`
              : `Signing: Sale ${sale.id}`
          };
        })
      );
    }
  }

  showSuccessAndClearAfterTimeout() {
    this.toastr.success('Signature saved successfully');
    this.clearCurrentSignable();
  }

  cancelSignature() {
    this.toastr.info(
      'Signature collection has been cancelled for this document',
      'Signature Cancelled',
      {
        timeOut: 15_000
      }
    );
    this.clearCurrentSignable();
  }

  private clearCurrentSignable() {
    this.signableResource.next(null);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
